import { t } from "@lingui/macro";
import type { DailyCallStats, ServiceTag } from "@repo/common";
import { Tag } from "lucide-react";
import { useMemo } from "react";
import { last30Days } from "../utils/dashboardStats";
import { normalizeTagValue } from "../utils/tagValues";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "./ui/card";

/** Maps ServiceTagColor to a Tailwind background + text class pair. */
const COLOR_CLASSES: Record<string, { bg: string; text: string }> = {
	slate: {
		bg: "bg-slate-100 dark:bg-slate-900/50",
		text: "text-slate-700 dark:text-slate-300",
	},
	gray: { bg: "bg-muted", text: "text-muted-foreground" },
	blue: { bg: "bg-accent", text: "text-accent-ink" },
	cyan: {
		bg: "bg-cyan-100 dark:bg-cyan-950/40",
		text: "text-cyan-700 dark:text-cyan-300",
	},
	teal: {
		bg: "bg-teal-100 dark:bg-teal-950/40",
		text: "text-teal-700 dark:text-teal-300",
	},
	green: {
		bg: "bg-live-tint",
		text: "text-live",
	},
	yellow: {
		bg: "bg-yellow-100 dark:bg-yellow-950/40",
		text: "text-yellow-700 dark:text-yellow-300",
	},
	amber: {
		bg: "bg-warn-tint",
		text: "text-warn",
	},
	orange: {
		bg: "bg-orange-100 dark:bg-orange-950/40",
		text: "text-orange-700 dark:text-orange-300",
	},
	red: {
		bg: "bg-red-100 dark:bg-red-950/40",
		text: "text-red-700 dark:text-red-300",
	},
	pink: {
		bg: "bg-pink-100 dark:bg-pink-950/40",
		text: "text-pink-700 dark:text-pink-300",
	},
	purple: {
		bg: "bg-purple-100 dark:bg-purple-950/40",
		text: "text-purple-700 dark:text-purple-300",
	},
	indigo: {
		bg: "bg-indigo-100 dark:bg-indigo-950/40",
		text: "text-indigo-700 dark:text-indigo-300",
	},
};

const DEFAULT_COLORS = { bg: "bg-muted", text: "text-muted-foreground" };

interface TagCountersProps {
	dailyStats: DailyCallStats[];
	serviceTags: ServiceTag[];
}

export function TagCounters({ dailyStats, serviceTags }: TagCountersProps) {
	const { start: cutoff, end } = last30Days();
	// Build a tag-id → label + color lookup from the flat serviceTags list
	const tagMeta = useMemo(() => {
		const map = new Map<string, { label: unknown; color: string }>();
		for (const tag of serviceTags) {
			const tagId = normalizeTagValue(tag?.id);
			if (tagId) {
				map.set(tagId, { label: tag.label, color: tag.color ?? "gray" });
			}
		}
		return map;
	}, [serviceTags]);

	// Sum callCount per tag across all daily docs within the window, grouped by tag label
	const tagCounts = useMemo(() => {
		const counts = new Map<
			string,
			{ label: string; color: string; count: number }
		>();

		for (const doc of dailyStats) {
			if (doc.dateKey < cutoff || doc.dateKey > end) continue;
			const totals = doc.totals?.tags;
			if (!totals || Array.isArray(totals) || typeof totals !== "object")
				continue;

			for (const [rawTagId, metric] of Object.entries(totals)) {
				const tagId = normalizeTagValue(rawTagId);
				if (
					!tagId ||
					!metric ||
					typeof metric.callCount !== "number" ||
					metric.callCount <= 0
				)
					continue;

				const meta = tagMeta.get(tagId);
				const label = normalizeTagValue(meta ? meta.label : tagId);
				if (!label) continue;
				const color = meta?.color ?? "gray";

				const key = label.toLowerCase();
				const existing = counts.get(key);

				if (existing) {
					existing.count += metric.callCount;
				} else {
					counts.set(key, { label, color, count: metric.callCount });
				}
			}
		}

		// Sort descending by count
		return Array.from(counts.values()).sort((a, b) => b.count - a.count);
	}, [dailyStats, tagMeta, cutoff, end]);

	return (
		<Card className="h-full">
			<CardHeader className="border-b border-card-border pb-4">
				<CardTitle className="flex items-center gap-2 text-base">
					<Tag className="w-4 h-4" />
					<span>{t({ id: "callsByTag", message: "Calls by Tag" })}</span>
				</CardTitle>
				<CardDescription>
					{t({ id: "dashboard.last30Days", message: "Last 30 days" })}
				</CardDescription>
			</CardHeader>
			<CardContent>
				{tagCounts.length === 0 ? (
					<p className="text-sm text-muted-foreground text-center py-4">
						{t({
							id: "noTaggedCalls",
							message: "No tagged calls in this period",
						})}
					</p>
				) : (
					<div className="space-y-4">
						{tagCounts.map(({ label, color, count }) => {
							const colors = COLOR_CLASSES[color] ?? DEFAULT_COLORS;

							return (
								<div
									key={label}
									className="grid grid-cols-[minmax(0,1fr)_minmax(48px,1fr)_auto] items-center gap-3"
								>
									<span
										className={`rounded-full px-2 py-1 text-xs font-semibold break-words ${colors.bg} ${colors.text}`}
									>
										{label}
									</span>
									<span
										className="h-2 overflow-hidden rounded-full bg-input-background"
										aria-hidden="true"
									>
										<span
											className={`block h-full rounded-full ${colors.text}`}
											style={{
												width: `${(count / tagCounts[0].count) * 100}%`,
												backgroundColor: "currentColor",
											}}
										/>
									</span>
									<span className="inline-flex items-center justify-center rounded-full bg-card/60 min-w-[1.4rem] h-5 px-1 text-xs font-semibold tabular-nums">
										{count}
									</span>
								</div>
							);
						})}
					</div>
				)}
			</CardContent>
		</Card>
	);
}
